const BACKEND_URI = "";

import {
    Config,
    HistoryApiResponse,
    SpeechRate,
    UserSettingsApiResponse,
    FeedbackListApiResponse,
    FeedbackItemApiResponse,
    ChatHistoryListApiResponse,
    IdeaListApiResponse,
    IdeaItem,
    IdeaReviewStatus,
    BuildInfo,
    TouchpointAnalyticsResponse,
    SearchAdminRequest,
    SearchAdminResponse,
    FilterOptionsApiResponse,
    ExampleQuestionsApiResponse,
    Domain,
    V2ChatRequest,
    V2ChatHistoryListApiResponse,
    V2ChatHistoryApiResponse,
    AuthorizationApiResponse
} from "./models";
import { useLogin } from "../authConfig";

export async function getHeaders(idToken: string | undefined): Promise<Record<string, string>> {
    // If using login add the id token of the logged in account as the authorization
    if (useLogin) {
        if (idToken) {
            return { Authorization: `Bearer ${idToken}` };
        }
    }

    return {};
}

export class ApiError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = "ApiError";
        this.status = status;
    }
}

type RequestOptions = {
    // Prefix of the error message thrown on a non-2xx response, e.g. "Getting ideas failed"
    errorMessage: string;
    method?: "GET" | "POST" | "PATCH" | "DELETE";
    idToken?: string;
    query?: URLSearchParams;
    // Serialized as JSON; sets the JSON Content-Type header when present
    body?: unknown;
    headers?: Record<string, string>;
    signal?: AbortSignal;
};

async function send(path: string, { errorMessage, method = "GET", idToken, query, body, headers, signal }: RequestOptions): Promise<Response> {
    const queryString = query?.toString();
    const response = await fetch(`${BACKEND_URI}${path}${queryString ? `?${queryString}` : ""}`, {
        method,
        headers: {
            ...(await getHeaders(idToken)),
            ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
            ...headers
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal
    });

    if (!response.ok) {
        throw new ApiError(response.status, `${errorMessage}: ${response.statusText || response.status}`);
    }

    return response;
}

export async function request<T>(path: string, options: RequestOptions): Promise<T> {
    const response = await send(path, options);
    return (await response.json()) as T;
}

async function requestBlob(path: string, options: RequestOptions): Promise<Blob> {
    const response = await send(path, options);
    return await response.blob();
}

export async function configApi(): Promise<Config> {
    return request<Config>("/config", { errorMessage: "Getting config failed" });
}

export async function versionApi(): Promise<BuildInfo> {
    return request<BuildInfo>("/version", { errorMessage: "Getting version failed" });
}

export async function getSpeechApi(text: string, speechRate: SpeechRate): Promise<string | null> {
    return await fetch("/speech", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            text: text,
            speechRate: speechRate
        })
    })
        .then(response => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                console.log("Speech synthesis is not enabled.");
                return null;
            } else {
                console.error("Unable to get speech synthesis.");
                return null;
            }
        })
        .then(blob => (blob ? URL.createObjectURL(blob) : null));
}

export function getSstWebSocketUrl(idToken: string, sampleRate: number): string {
    // Browsers can't set an Authorization header on a WebSocket handshake,
    // so the bearer token is passed via the ?token= query fallback.
    const base = BACKEND_URI || window.location.origin;
    const wsBase = base.replace(/^http/, "ws");
    const params = new URLSearchParams({ token: idToken, sampleRate: String(sampleRate) });
    return `${wsBase}/v2/sst/wss?${params}`;
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
}

export async function getChatHistoryListApi(
    count: number,
    continuationToken: string | undefined,
    idToken: string,
    domain?: Domain
): Promise<V2ChatHistoryListApiResponse> {
    return request<V2ChatHistoryListApiResponse>("/v2/chat/history", {
        errorMessage: "Getting chat histories failed",
        method: "POST",
        idToken,
        body: { count, continuationToken: continuationToken ?? null, ...(domain ? { domain } : {}) }
    });
}

export async function getChatHistoryApi(id: string, idToken: string): Promise<V2ChatHistoryApiResponse> {
    return request<V2ChatHistoryApiResponse>(`/v2/chat/history/${id}`, {
        errorMessage: "Getting chat history failed",
        method: "POST",
        idToken
    });
}

export async function deleteChatHistoryApi(id: string, idToken: string): Promise<void> {
    await send(`/v2/chat/history/${id}`, { errorMessage: "Deleting chat history failed", method: "DELETE", idToken });
}

export async function getUserSettingsApi(idToken: string): Promise<UserSettingsApiResponse> {
    const dataResponse = await request<{ settings: UserSettingsApiResponse }>("/usersettings", {
        errorMessage: "Getting user settings failed",
        idToken
    });
    return dataResponse.settings;
}

export async function getAuthorizationApi(idToken: string, signal?: AbortSignal): Promise<AuthorizationApiResponse> {
    return request<AuthorizationApiResponse>("/authorization", { errorMessage: "Getting authorization failed", idToken, signal });
}

export async function postV2FeedbackApi(
    conversationId: string,
    positive: boolean,
    turnIndex: number,
    comment: string | undefined,
    idToken: string,
    reason?: string
): Promise<void> {
    await send(`/v2/chat/feedback/${encodeURIComponent(conversationId)}`, {
        errorMessage: "Posting v2 feedback failed",
        method: "POST",
        idToken,
        body: { positive, turnIndex, comment, reason }
    });
}

export async function getFeedbackApi(
    idToken: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortDir: "asc" | "desc",
    search?: string,
    startDate?: string,
    endDate?: string,
    userId?: string,
    timezone?: string, // IANA timezone string
    reviewStatus?: "reviewed" | "not_reviewed" | "all",
    failureTypes?: string[],
    signal?: AbortSignal
): Promise<FeedbackListApiResponse> {
    const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_dir: sortDir
    });
    if (search && search.trim()) {
        params.append("search", search.trim());
    }
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (userId && userId.trim()) {
        params.append("user_id", userId.trim());
    }
    if (timezone) {
        params.append("timezone", timezone);
    }
    if (reviewStatus && reviewStatus !== "all") {
        params.append("review_status", reviewStatus);
    }
    if (failureTypes && failureTypes.length > 0) {
        params.append("failure_types", failureTypes.join(","));
    }

    return request<FeedbackListApiResponse>("/feedbacklist", { errorMessage: "Getting user feedback failed", idToken, query: params, signal });
}

export async function getFeedbackByIdApi(id: string, idToken: string): Promise<FeedbackItemApiResponse> {
    return request<FeedbackItemApiResponse>(`/feedback/${id}`, { errorMessage: "Getting feedback failed", idToken });
}

export async function deleteFeedbackItemApi(id: string, idToken: string): Promise<void> {
    await send(`/feedback/${id}`, { errorMessage: "Deleting feedback item failed", method: "DELETE", idToken });
}

function patchFeedbackApi(id: string, body: Record<string, unknown>, errorMessage: string, idToken: string): Promise<FeedbackItemApiResponse> {
    return request<FeedbackItemApiResponse>(`/feedback/${id}`, { errorMessage, method: "PATCH", idToken, body });
}

export async function updateFeedbackJiraTicketApi(id: string, jiraTicket: string | null, idToken: string): Promise<FeedbackItemApiResponse> {
    return patchFeedbackApi(id, { jira_ticket: jiraTicket }, "Updating feedback failed", idToken);
}

export async function updateFeedbackReviewStatusApi(id: string, reviewStatus: "reviewed" | null, idToken: string): Promise<FeedbackItemApiResponse> {
    return patchFeedbackApi(id, { review_status: reviewStatus }, "Updating feedback review status failed", idToken);
}

export async function updateFeedbackFailureTypeApi(id: string, failureType: string | null, idToken: string): Promise<FeedbackItemApiResponse> {
    return patchFeedbackApi(id, { failure_type: failureType }, "Updating feedback failure type failed", idToken);
}

export async function updateFeedbackCommentApi(id: string, comment: string | null, idToken: string): Promise<FeedbackItemApiResponse> {
    return patchFeedbackApi(id, { comment }, "Updating feedback comment failed", idToken);
}

export async function exportFeedbackCsvApi(
    idToken: string,
    sortBy: string,
    sortDir: "asc" | "desc",
    search?: string,
    start?: string, // YYYY-MM-DD
    end?: string, // YYYY-MM-DD
    userId?: string,
    timezone?: string // IANA timezone string
): Promise<Blob> {
    const params = new URLSearchParams({
        sort_by: sortBy,
        sort_dir: sortDir
    });
    if (search && search.trim()) {
        params.append("search", search.trim());
    }
    if (start) params.append("start_date", start);
    if (end) params.append("end_date", end);
    if (userId) params.append("user_id", userId);
    if (timezone) params.append("timezone", timezone);
    if (userId && userId.trim()) {
        params.append("user_id", userId.trim());
    }

    return requestBlob("/feedback/export", { errorMessage: "Exporting feedback failed", idToken, query: params });
}

export type FeedbackAnalyticsData = {
    name: string;
    positive: number;
    negative: number;
    date_key: string;
};

export type FeedbackAnalyticsResponse = {
    data: FeedbackAnalyticsData[];
    aggregation: "day" | "week" | "month" | "year";
    start_date: string;
    end_date: string;
    total_items: number;
    available_countries?: string[];
    available_domains?: string[];
    available_touchpoints?: string[];
    sql_query?: string;
};

export async function getFeedbackAnalyticsApi(
    idToken: string,
    aggregation: "day" | "week" | "month" | "year",
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<FeedbackAnalyticsResponse> {
    const params = new URLSearchParams({
        aggregation: aggregation
    });
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (timezone) {
        params.append("timezone", timezone);
    }
    if (countries && countries.length > 0) {
        params.append("countries", countries.join(","));
    }
    if (touchpoints && touchpoints.length > 0) {
        // Use triple pipe delimiter to avoid conflicts with commas in user-agent strings
        params.append("touchpoints", touchpoints.join("|||"));
    }
    if (entraOid) {
        params.append("entra_oid", entraOid);
    }
    if (domains && domains.length > 0) {
        params.append("domains", domains.join(","));
    }

    return request<FeedbackAnalyticsResponse>("/analytics/feedback", { errorMessage: "Getting feedback analytics failed", idToken, query: params });
}

// Conversations Analytics Types (formerly History Analytics)
export interface ConversationsAnalyticsData {
    name: string;
    total: number;
    unique_users?: number; // Optional for backwards compatibility
    messages?: number; // Optional for backwards compatibility
    date_key: string;
}

export interface ConversationsAnalyticsResponse {
    data: ConversationsAnalyticsData[];
    aggregation: "day" | "week" | "month" | "year";
    start_date: string;
    end_date: string;
    total_items: number;
    total_unique_users?: number;
    total_messages?: number;
    available_countries?: string[];
    available_domains?: string[];
    available_touchpoints?: string[];
    sql_query?: string;
}

// Chat Messages Analytics Types (formerly History Messages Analytics)
export interface ChatMessagesAnalyticsData {
    name: string;
    average: number;
    total_messages: number;
    total_conversations: number;
    date_key: string;
}

export interface ChatMessagesAnalyticsResponse {
    data: ChatMessagesAnalyticsData[];
    aggregation: "day" | "week" | "month" | "year";
    start_date: string;
    end_date: string;
    total_items: number;
    available_countries?: string[];
    available_domains?: string[];
    available_touchpoints?: string[];
    sql_query?: string;
}

export async function getConversationsAnalyticsApi(
    idToken: string,
    aggregation: "day" | "week" | "month" | "year",
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<ConversationsAnalyticsResponse> {
    const params = new URLSearchParams({
        aggregation: aggregation
    });
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (timezone) {
        params.append("timezone", timezone);
    }
    if (countries && countries.length > 0) {
        params.append("countries", countries.join(","));
    }
    if (touchpoints && touchpoints.length > 0) {
        // Use triple pipe delimiter to avoid conflicts with commas in user-agent strings
        params.append("touchpoints", touchpoints.join("|||"));
    }
    if (entraOid) {
        params.append("entra_oid", entraOid);
    }
    if (domains && domains.length > 0) {
        params.append("domains", domains.join(","));
    }

    return request<ConversationsAnalyticsResponse>("/analytics/conversations", {
        errorMessage: "Getting conversations analytics failed",
        idToken,
        query: params
    });
}

export async function getChatMessagesAnalyticsApi(
    idToken: string,
    aggregation: "day" | "week" | "month" | "year",
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<ChatMessagesAnalyticsResponse> {
    const params = new URLSearchParams({
        aggregation: aggregation
    });
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (timezone) {
        params.append("timezone", timezone);
    }
    if (countries && countries.length > 0) {
        params.append("countries", countries.join(","));
    }
    if (touchpoints && touchpoints.length > 0) {
        // Use triple pipe delimiter to avoid conflicts with commas in user-agent strings
        params.append("touchpoints", touchpoints.join("|||"));
    }
    if (entraOid) {
        params.append("entra_oid", entraOid);
    }
    if (domains && domains.length > 0) {
        params.append("domains", domains.join(","));
    }

    return request<ChatMessagesAnalyticsResponse>("/analytics/chat-messages", {
        errorMessage: "Getting chat messages analytics failed",
        idToken,
        query: params
    });
}

// Peak Hours Analytics Types
export interface PeakHoursAnalyticsData {
    hour: number;
    activity_count: number;
    unique_users: number;
    percentage: number;
}

export interface PeakHoursAnalyticsResponse {
    data: PeakHoursAnalyticsData[];
    start_date: string;
    end_date: string;
    total_activities: number;
    peak_hour: number;
    available_countries?: string[];
    available_domains?: string[];
    available_touchpoints?: string[];
    sql_query?: string;
}

export async function getPeakHoursAnalyticsApi(
    idToken: string,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<PeakHoursAnalyticsResponse> {
    const params = new URLSearchParams();
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (timezone) {
        params.append("timezone", timezone);
    }
    if (countries && countries.length > 0) {
        params.append("countries", countries.join(","));
    }
    if (touchpoints && touchpoints.length > 0) {
        // Use triple pipe delimiter to avoid conflicts with commas in user-agent strings
        params.append("touchpoints", touchpoints.join("|||"));
    }
    if (entraOid) {
        params.append("entra_oid", entraOid);
    }
    if (domains && domains.length > 0) {
        params.append("domains", domains.join(","));
    }

    return request<PeakHoursAnalyticsResponse>("/analytics/peak-hours", { errorMessage: "Getting peak hours analytics failed", idToken, query: params });
}

export async function exportHistoryApi(
    idToken: string,
    sortBy?: string,
    sortDir?: "asc" | "desc",
    search?: string,
    start?: string, // YYYY-MM-DD
    end?: string, // YYYY-MM-DD
    userId?: string,
    timezone?: string // IANA timezone string
): Promise<Blob> {
    const params = new URLSearchParams();
    if (start) params.append("start_date", start);
    if (end) params.append("end_date", end);
    if (sortBy) params.append("sort_by", sortBy);
    if (sortDir) params.append("sort_dir", sortDir);
    if (search) params.append("search", search);
    if (userId) params.append("user_id", userId);
    if (timezone) params.append("timezone", timezone);

    return requestBlob("/history/export", { errorMessage: "Exporting history failed", idToken, query: params });
}

export async function getAdminChatHistoryListApi(
    idToken: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortDir: "asc" | "desc",
    search?: string,
    startDate?: string,
    endDate?: string,
    userId?: string,
    timezone?: string, // IANA timezone string
    signal?: AbortSignal
): Promise<ChatHistoryListApiResponse> {
    const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_dir: sortDir
    });
    if (search && search.trim()) {
        params.append("search", search.trim());
    }
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (userId) {
        params.append("user_id", userId);
    }
    if (timezone) {
        params.append("timezone", timezone);
    }

    return request<ChatHistoryListApiResponse>("/history/list", { errorMessage: "Getting user history failed", idToken, query: params, signal });
}

export async function getChatHistoryItemAdminApi(id: string, idToken: string): Promise<HistoryApiResponse> {
    return request<HistoryApiResponse>(`/history/items/${id}`, { errorMessage: "Getting admin chat history failed", idToken });
}

export async function deleteChatHistoryItemAdminApi(id: string, entraOid: string, idToken: string): Promise<void> {
    const params = new URLSearchParams({ entra_oid: entraOid });
    await send(`/history/items/${id}?${params.toString()}`, { errorMessage: "Deleting chat history item failed", method: "DELETE", idToken });
}

export async function getChatHistoryItemDatabricksTraceUrlApi(id: string, idToken: string): Promise<string> {
    const { url } = await request<{ url: string }>(`/history/items/${id}/databricks-trace-url`, {
        errorMessage: "Getting Databricks trace link failed",
        idToken
    });
    return url;
}

// User Admin API Functions

export interface UserListItem {
    name: string;
    username: string;
    entra_oid: string;
}

export interface UserListApiResponse {
    users: UserListItem[];
    total_items: number;
    total_pages: number;
    page: number;
    page_size: number;
}

export interface UserDetailsApiResponse {
    id: string;
    entra_oid: string;
    name: string;
    username: string;
    activity_stats: {
        total_conversations: number;
        total_messages: number;
        last_activity: string | null;
        first_activity: string | null;
    };
}

export interface UserActivityAnalyticsData {
    name: string;
    username: string;
    entra_oid: string;
    total_messages: number;
    total_conversations: number;
    average_messages: number;
}

export interface UserActivityAnalyticsResponse {
    data: UserActivityAnalyticsData[];
    start_date: string;
    end_date: string;
    total_users: number;
    top_users: number;
    available_countries?: string[];
    available_domains?: string[];
    available_touchpoints?: string[];
    sql_query?: string;
}

export async function getUsersListApi(
    idToken: string,
    page: number,
    pageSize: number,
    sortBy?: string,
    sortDir?: "asc" | "desc",
    search?: string,
    startDate?: string, // YYYY-MM-DD
    endDate?: string, // YYYY-MM-DD
    signal?: AbortSignal
): Promise<UserListApiResponse> {
    const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
    });
    if (sortBy) {
        params.append("sort_by", sortBy);
    }
    if (sortDir) {
        params.append("sort_dir", sortDir);
    }
    if (search) {
        params.append("search", search);
    }
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }

    return request<UserListApiResponse>("/users/list", { errorMessage: "Getting users list failed", idToken, query: params, signal });
}

export async function getUserDetailsApi(idToken: string, userId: string): Promise<UserDetailsApiResponse> {
    return request<UserDetailsApiResponse>(`/users/${userId}`, { errorMessage: "Getting user details failed", idToken });
}

export async function exportUsersApi(
    idToken: string,
    startDate?: string, // YYYY-MM-DD
    endDate?: string // YYYY-MM-DD
): Promise<Blob> {
    const params = new URLSearchParams();
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }

    return requestBlob("/users/export", { errorMessage: "Export failed", idToken, query: params });
}

export async function getUserActivityAnalyticsApi(
    idToken: string,
    topUsers: number = 10,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<UserActivityAnalyticsResponse> {
    const params = new URLSearchParams({
        top_users: topUsers.toString()
    });
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (countries && countries.length > 0) {
        params.append("countries", countries.join(","));
    }
    if (touchpoints && touchpoints.length > 0) {
        params.append("touchpoints", touchpoints.join("|||"));
    }
    if (entraOid) {
        params.append("entra_oid", entraOid);
    }
    if (domains && domains.length > 0) {
        params.append("domains", domains.join(","));
    }

    return request<UserActivityAnalyticsResponse>("/analytics/user-activity", {
        errorMessage: "Getting user activity analytics failed",
        idToken,
        query: params
    });
}

export async function getTouchpointAnalyticsApi(
    idToken: string,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[],
    entraOid?: string,
    domains?: string[]
): Promise<TouchpointAnalyticsResponse> {
    const params = new URLSearchParams();

    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (timezone) {
        params.append("timezone", timezone);
    }
    if (countries && countries.length > 0) {
        params.append("countries", countries.join(","));
    }
    if (entraOid) {
        params.append("entra_oid", entraOid);
    }
    if (domains && domains.length > 0) {
        params.append("domains", domains.join(","));
    }

    return request<TouchpointAnalyticsResponse>("/analytics/touchpoints", { errorMessage: "Getting touchpoint analytics failed", idToken, query: params });
}

export async function chatV2StreamApi(request: V2ChatRequest, idToken: string | undefined, signal: AbortSignal): Promise<globalThis.Response> {
    const headers = await getHeaders(idToken);
    return await fetch(`${BACKEND_URI}/v2/chat/stream`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        signal
    });
}

export async function getExampleQuestionsApi(idToken: string | undefined, language: string, domain: Domain): Promise<ExampleQuestionsApiResponse> {
    return request<ExampleQuestionsApiResponse>(`/v2/examplequestion?domain=${encodeURIComponent(domain)}`, {
        errorMessage: "Getting example questions failed",
        idToken,
        headers: { "Accept-Language": language }
    });
}

export async function getFilterOptionsApi(idToken: string | undefined, language: string, domain: Domain): Promise<FilterOptionsApiResponse> {
    return request<FilterOptionsApiResponse>(`/v2/chat/userfilter?domain=${encodeURIComponent(domain)}`, {
        errorMessage: "Getting filter options failed",
        idToken,
        headers: { "Accept-Language": language }
    });
}

export async function searchAdminApi(searchRequest: SearchAdminRequest, idToken: string | undefined): Promise<SearchAdminResponse> {
    return request<SearchAdminResponse>("/admin_ai_search/search", {
        errorMessage: "Azure AI Search admin request failed",
        method: "POST",
        idToken,
        body: searchRequest
    });
}

export async function getIdeasApi(
    idToken: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortDir: "asc" | "desc",
    search?: string,
    startDate?: string,
    endDate?: string,
    userId?: string,
    timezone?: string,
    categoryFilter?: string,
    includeDrafts?: boolean,
    reviewStatus?: IdeaReviewStatus | "all",
    signal?: AbortSignal
): Promise<IdeaListApiResponse> {
    const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_dir: sortDir
    });
    if (search && search.trim()) {
        params.append("search", search.trim());
    }
    if (startDate) {
        params.append("start_date", startDate);
    }
    if (endDate) {
        params.append("end_date", endDate);
    }
    if (userId && userId.trim()) {
        params.append("user_id", userId.trim());
    }
    if (timezone) {
        params.append("timezone", timezone);
    }
    if (categoryFilter && categoryFilter.trim()) {
        params.append("category_filter", categoryFilter.trim());
    }
    if (includeDrafts) {
        params.append("include_drafts", "true");
    }
    if (reviewStatus && reviewStatus !== "all") {
        params.append("review_status", reviewStatus);
    }

    return request<IdeaListApiResponse>("/idealist", { errorMessage: "Getting ideas failed", idToken, query: params, signal });
}

export async function getIdeaByIdApi(id: string, idToken: string): Promise<IdeaItem> {
    return request<IdeaItem>(`/idea/${encodeURIComponent(id)}`, { errorMessage: "Getting idea failed", idToken });
}

function patchIdeaApi(id: string, body: Record<string, unknown>, errorMessage: string, idToken: string): Promise<IdeaItem> {
    return request<IdeaItem>(`/idea/${encodeURIComponent(id)}`, { errorMessage, method: "PATCH", idToken, body });
}

export async function updateIdeaReviewStatusApi(id: string, reviewStatus: IdeaReviewStatus, idToken: string): Promise<IdeaItem> {
    return patchIdeaApi(id, { review_status: reviewStatus }, "Updating idea review status failed", idToken);
}

export async function updateIdeaJiraLinkApi(id: string, jiraLink: string | null, idToken: string): Promise<IdeaItem> {
    return patchIdeaApi(id, { jira_link: jiraLink }, "Updating idea Jira link failed", idToken);
}

export async function updateIdeaReviewCommentApi(id: string, reviewComment: string | null, idToken: string): Promise<IdeaItem> {
    return patchIdeaApi(id, { review_comment: reviewComment }, "Updating idea review comment failed", idToken);
}

export async function exportIdeasApi(
    idToken: string,
    sortBy: string,
    sortDir: "asc" | "desc",
    search?: string,
    start?: string,
    end?: string,
    userId?: string,
    timezone?: string,
    reviewStatus?: IdeaReviewStatus | "all"
): Promise<Blob> {
    const params = new URLSearchParams({
        sort_by: sortBy,
        sort_dir: sortDir
    });
    if (search && search.trim()) {
        params.append("search", search.trim());
    }
    if (start) params.append("start_date", start);
    if (end) params.append("end_date", end);
    if (userId && userId.trim()) {
        params.append("user_id", userId.trim());
    }
    if (timezone) params.append("timezone", timezone);
    if (reviewStatus && reviewStatus !== "all") params.append("review_status", reviewStatus);

    return requestBlob("/idea/export", { errorMessage: "Exporting ideas failed", idToken, query: params });
}
